import { useReducedMotion } from 'motion/react';
import { type FocusEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { ONE_SECOND } from '@air/utilities';

/** Sub-pixel scroll offsets at either end still count as the end. */
const SCROLL_EDGE_TOLERANCE_PX = 1;

/** Long enough to read a card's title and subtitle before the row moves on. */
const AUTO_ADVANCE_INTERVAL_SECONDS = 6;
const AUTO_ADVANCE_INTERVAL_MS = AUTO_ADVANCE_INTERVAL_SECONDS * ONE_SECOND;

type WorkspaceAgentPromptCardCarouselScrollDirection = 'next' | 'prev';

/**
 * Previous/next paging for the prompt-card row. Each press lands on the adjacent card's start edge,
 * where scroll snap would settle it anyway, inset by the list's scroll padding. The list must be
 * `relative` so a card's `offsetLeft` is measured from the list rather than from some ancestor.
 *
 * The row also advances itself one card at a time and loops back to the start, pausing while the
 * pointer is over it or focus is inside it (spread `autoAdvancePauseProps` on the carousel root) and
 * staying still for reduced motion.
 */
export const useWorkspaceAgentPromptCardCarouselScroll = () => {
  const [scrollContainer, setScrollContainer] = useState<HTMLOListElement | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  useIsomorphicLayoutEffect(() => {
    if (!scrollContainer) {
      return;
    }

    const updateScrollEdges = () => {
      const { clientWidth, scrollLeft, scrollWidth } = scrollContainer;

      setCanScrollPrev(scrollLeft > SCROLL_EDGE_TOLERANCE_PX);
      setCanScrollNext(scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_TOLERANCE_PX);
    };

    updateScrollEdges();

    scrollContainer.addEventListener('scroll', updateScrollEdges, { passive: true });

    const observer = new ResizeObserver(updateScrollEdges);

    observer.observe(scrollContainer);

    return () => {
      scrollContainer.removeEventListener('scroll', updateScrollEdges);
      observer.disconnect();
    };
  }, [scrollContainer]);

  const scrollToAdjacentCard = useCallback(
    (direction: WorkspaceAgentPromptCardCarouselScrollDirection) => {
      if (!scrollContainer) {
        return;
      }

      const { scrollLeft } = scrollContainer;
      const scrollPaddingLeft = Number.parseFloat(getComputedStyle(scrollContainer).scrollPaddingLeft) || 0;
      const cardScrollLefts = Array.from(
        scrollContainer.querySelectorAll<HTMLElement>(':scope > [data-prompt-card-slide]'),
        (card) => card.offsetLeft - scrollPaddingLeft,
      );
      const targetScrollLeft =
        direction === 'next'
          ? cardScrollLefts.find((cardScrollLeft) => cardScrollLeft > scrollLeft + SCROLL_EDGE_TOLERANCE_PX)
          : cardScrollLefts.findLast((cardScrollLeft) => cardScrollLeft < scrollLeft - SCROLL_EDGE_TOLERANCE_PX);

      if (targetScrollLeft === undefined) {
        return;
      }

      scrollContainer.scrollTo({ behavior: shouldReduceMotion ? 'auto' : 'smooth', left: targetScrollLeft });
    },
    [scrollContainer, shouldReduceMotion],
  );

  const handleScrollPrev = useCallback(() => scrollToAdjacentCard('prev'), [scrollToAdjacentCard]);
  const handleScrollNext = useCallback(() => scrollToAdjacentCard('next'), [scrollToAdjacentCard]);

  const isAutoAdvancePaused = isHovered || isFocusWithin;

  useEffect(() => {
    if (!scrollContainer || isAutoAdvancePaused || shouldReduceMotion) {
      return;
    }

    const intervalId = window.setInterval(() => {
      const { clientWidth, scrollLeft, scrollWidth } = scrollContainer;

      if (scrollLeft + clientWidth >= scrollWidth - SCROLL_EDGE_TOLERANCE_PX) {
        scrollContainer.scrollTo({ behavior: 'smooth', left: 0 });
        return;
      }

      scrollToAdjacentCard('next');
    }, AUTO_ADVANCE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isAutoAdvancePaused, scrollContainer, scrollToAdjacentCard, shouldReduceMotion]);

  const autoAdvancePauseProps = useMemo(
    () => ({
      onBlur: (event: FocusEvent<HTMLElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsFocusWithin(false);
        }
      },
      onFocus: () => setIsFocusWithin(true),
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    }),
    [],
  );

  return {
    autoAdvancePauseProps,
    canScrollNext,
    canScrollPrev,
    handleScrollNext,
    handleScrollPrev,
    scrollContainerRef: setScrollContainer,
  };
};
